import { LanguageSettings } from '@phrasee/phrasee-typings/Graphql/interfaces'
import { gql } from 'graphql-request'

import { connectInternalApi, fetchGraphQL } from 'common/api'

export type ScoreExperiment = {
  id: string
  name: string
  sendDate?: string
  distributionChannel?: string
}

type ExperimentResponse = {
  id: string
  name: string
  send_date?: string
  campaign_configuration?: {
    distribution_channel?: string
  }
}

type ExperimentsResponse = {
  campaigns: ExperimentResponse[]
  count: number
}

export const fetchProjectExperiments = async ({
  projectId,
  accountId,
}: {
  projectId: string
  accountId: string
}): Promise<ScoreExperiment[]> => {
  const query = gql`
    query languageScoreExperiments(
      $page: Int
      $pageSize: Int
      $filter: CampaignFilter
    ) {
      data: campaigns(page: $page, pageSize: $pageSize, filter: $filter) {
        campaigns {
          id: _id
          name
          send_date
          campaign_configuration {
            distribution_channel
          }
        }
        count
      }
    }
  `

  const variables = {
    page: 1,
    pageSize: 200,
    filter: {
      accountId,
      projectId,
    },
  }

  const result = await fetchGraphQL<ExperimentsResponse>({ query, variables })

  return (result.campaigns ?? []).map((campaign) => ({
    id: campaign.id,
    name: campaign.name,
    sendDate: campaign.send_date,
    distributionChannel: campaign.campaign_configuration?.distribution_channel,
  }))
}

export type ScoreVariant = {
  campaignId: string
  text: string
}

type VariantsResponse = ScoreVariant[]

export const fetchExperimentVariants = async (
  campaignIds: string[]
): Promise<ScoreVariant[]> => {
  const query = gql`
    query languageScoreVariants($filter: VariantFilter) {
      data: variants(filter: $filter) {
        campaignId: campaign_id
        text
      }
    }
  `

  const variables = {
    filter: {
      campaignsList: campaignIds,
    },
  }

  return fetchGraphQL<VariantsResponse>({ query, variables })
}

export type ScoreTone = {
  name: string
  description?: string
  instruction?: string
  examples?: string[]
}

export type ScoreContentRequest = {
  content: string
  iso_language?: string
  sl_id?: string
  campaign_id?: string
  language_settings?: LanguageSettings
  tones?: ScoreTone[]
  brief?: string
}

export type LanguageRuleResult = {
  rule_id: string
  iso_language: string
  rule: string
  applicable: boolean
  compliant: boolean
  confidence: number
  explanation: string
  offending_text: string | null
  suggestion: string | null
}

export type SettingsCheckResult = {
  check_id: string
  field: string
  applicable: boolean
  compliant: boolean
  explanation: string
  offending_text: string | null
  suggestion: string | null
}

export type FluencyResult = {
  score: number
  explanation: string
  issues?: unknown[]
}

export type LanguageResult = {
  score: number
  rules_score?: number
  fluency?: FluencyResult
  rules_evaluated: number
  applicable_rules: number
  passed: number
  failed: number
  results: LanguageRuleResult[]
}

export type SettingsResult = {
  iso_language_requested?: string
  settings_checks: SettingsCheckResult[]
  applicable: number
  passed: number
  failed: number
  score: number
  method?: string
}

export type ToneResult = {
  name: string
  score: number
  explanation: string
}

export type BrandResult = {
  overall_score: number
  tone_results: ToneResult[]
}

export type BriefResult = {
  score: number
  explanation: string
}

export type ScoreContentResponse = {
  content: string
  iso_language_requested: string
  language_result?: LanguageResult
  brand_result?: BrandResult
  settings_result?: SettingsResult
  brief_result?: BriefResult
  scores: {
    language?: number
    brand_tone?: number
    language_settings?: number
    brief?: number
    average: number
  }
  scoring_model: string
  latency_ms: number
}

export const scoreContent = async (
  payload: ScoreContentRequest
): Promise<ScoreContentResponse> => {
  const result = await connectInternalApi.post<ScoreContentResponse>(
    `combined/score`,
    payload
  )
  return result.data
}

type ProjectConfigResponse = {
  project?: {
    brand_voice_id?: string
  }
}

type CombinedLanguageSettingsResponse = {
  data: LanguageSettings
}

type BrandVoiceDetailResponse = {
  brandVoice?: {
    brand_tones?: {
      name?: string
      description?: string
      instruction?: string
      examples?: (string | null)[]
    }[]
  }
}

export type ProjectScoringConfig = {
  isoLanguage?: string
  languageSettings?: LanguageSettings
  tones: ScoreTone[]
}

// Assembles the scoring payload inputs (language settings + tones) for the
// selected project. Language settings resolve from the project's region /
// account / brand voice; tones come from the project's brand voice if it has
// one. Both are sent best-effort — the scoring service tolerates missing parts.
export const fetchProjectScoringConfig = async ({
  accountId,
  projectId,
}: {
  accountId: string
  projectId: string
}): Promise<ProjectScoringConfig> => {
  const projectConfig = await connectInternalApi.get<ProjectConfigResponse>(
    `v1/core/marge/accounts/${accountId}/projects/${projectId}?returnMappedUIFields=1`
  )

  const brandVoiceId = projectConfig.data.project?.brand_voice_id

  const [languageSettingsResult, brandVoiceResult] = await Promise.all([
    connectInternalApi.get<CombinedLanguageSettingsResponse>(
      `v1/core/monorail/projects/${projectId}/combined_language_settings`,
      {
        params: brandVoiceId ? { brand_voice_id: brandVoiceId } : {},
      }
    ),
    brandVoiceId
      ? connectInternalApi.get<BrandVoiceDetailResponse>(
          `v1/core/monorail/accounts/${accountId}/brand-voice/${brandVoiceId}`
        )
      : Promise.resolve(undefined),
  ])

  const languageSettings = languageSettingsResult.data.data

  const tones: ScoreTone[] = (
    brandVoiceResult?.data.brandVoice?.brand_tones ?? []
  ).map((tone) => ({
    name: tone.name ?? '',
    description: tone.description,
    instruction: tone.instruction,
    examples: tone.examples?.filter((e): e is string => !!e),
  }))

  return {
    isoLanguage: languageSettings?.iso_language ?? undefined,
    languageSettings,
    tones,
  }
}
